import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { EASE } from "../Reveal";

const inputCls =
  "w-full bg-[#0A0A0A] border border-[#28282D] rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-yellow-400 outline-none transition-colors";

const EMPTY = {
  name: "", phone: "", service: "", car_brand: "", car_model: "",
  tire_size: "", area: "", location_url: "", problem: "",
};

export const RequestModal = ({ open, prefill, services, areas, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setForm((f) => ({ ...EMPTY, ...prefill, name: f.name, phone: f.phone }));
  }, [open, prefill]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("فضلًا أدخل الاسم");
    if (!/^\+?\d[\d\s-]{8,13}$/.test(form.phone.trim())) return toast.error("فضلًا أدخل رقم جوال صحيح");
    setSending(true);
    try {
      await api.post("/requests", form);
      toast.success("تم استلام طلبك! سنتواصل معك خلال دقائق");
      setForm(EMPTY);
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="request-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-lg bg-[#121214] border border-yellow-400/30 rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-[#121214] border-b border-[#28282D] px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-white">اطلب الخدمة</h3>
              <button data-testid="request-close-btn" onClick={onClose} className="text-zinc-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="request-name-input" value={form.name} onChange={set("name")} placeholder="الاسم *" className={inputCls} />
                <input data-testid="request-phone-input" value={form.phone} onChange={set("phone")} placeholder="رقم الجوال *" dir="ltr" className={`${inputCls} text-left`} />
              </div>
              <select data-testid="request-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                <option value="">الخدمة المطلوبة</option>
                {services.map((s) => (
                  <option key={s.id} value={s.title}>{s.title}</option>
                ))}
                <option value="أخرى">أخرى</option>
              </select>
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="request-car-input" value={form.car_brand} onChange={set("car_brand")} placeholder="نوع السيارة" className={inputCls} />
                <input data-testid="request-model-input" value={form.car_model} onChange={set("car_model")} placeholder="الموديل" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="request-size-input" value={form.tire_size} onChange={set("tire_size")} placeholder="مقاس الإطار (اختياري)" dir="ltr" className={`${inputCls} text-left`} />
                <select data-testid="request-area-select" value={form.area} onChange={set("area")} className={inputCls}>
                  <option value="">المنطقة</option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.name}>{a.name}</option>
                  ))}
                  <option value="غير متأكد">غير متأكد</option>
                </select>
              </div>
              <input data-testid="request-location-input" value={form.location_url} onChange={set("location_url")} placeholder="رابط موقعك من جوجل ماب (اختياري)" dir="ltr" className={`${inputCls} text-left`} />
              <textarea data-testid="request-problem-input" value={form.problem} onChange={set("problem")} placeholder="وصف المشكلة — مثال: كفر منفجر، بطارية ما تشتغل..." rows={3} className={inputCls} />
              <button
                data-testid="request-submit-btn"
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold px-6 py-3.5 rounded-full hover:bg-yellow-300 transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {sending ? "جارٍ الإرسال..." : "إرسال الطلب"}
              </button>
              <p className="text-center text-xs text-zinc-600">بعد الإرسال نتصل بك مباشرة لتأكيد السعر والوصول</p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
